/**
 * The Three side of the house batcher.
 *
 * Takes what the core emits — one merged position/colour/index buffer set for a
 * whole tile — and makes it a single `Mesh`. One mesh, one material, one draw
 * call per streaming chunk, however many buildings are in it.
 *
 * `buildHouseChunk` is the direct entry point for callers that already have
 * oriented boxes in world space. The streaming pipeline does not use it: it
 * generates in the vector worker, where the merge is off the main thread, and
 * comes through `createHouseMesh` instead.
 */

import type { Box3, Sphere } from 'three';
import {
  BufferAttribute,
  BufferGeometry,
  Color,
  FrontSide,
  Mesh,
  MeshStandardMaterial,
  Vector3,
} from 'three';
import type { HouseBlock, HouseOptions } from '../core/build/houses.js';
import { buildHouses, isBuildableBlock } from '../core/build/houses.js';
import { lampLight } from './flora.js';
import { GLSL_SNOW_TOP, SNOW_TOP_COLOUR, terrainWeather } from './terrainMaterial.js';

/** A streamed building: an oriented box, a yaw, and a seed. */
/** Mapped attributes an item may carry, as on `HouseBlock`. */
type MappedFields = 'use' | 'roof' | 'floors' | 'floorHeight' | 'year' | 'wallColour' | 'roofColour' | 'towerHeight' | 'attic';

export interface HouseChunkItem extends Partial<Pick<HouseBlock, MappedFields>> {
  /** World-space bounds. `min.y` is ground level; the height is the eaves. */
  box: Box3;
  /** Yaw about Y, the sense of `Object3D.rotation.y`. */
  rotation: number;
  seed: number;
}

/**
 * One material for every house in the world.
 *
 * `vertexColors` rather than a palette texture: the colour is three bytes on a
 * vertex that has to exist anyway, which is cheaper to store than UVs and
 * cheaper to shade than a texture fetch, and it needs no custom shader — so
 * fog, tone mapping and any future shadow pass keep working untouched.
 *
 * `flatShading` is not a style choice that can be switched off. The geometry
 * carries **no normal attribute**: under `FLAT_SHADED` Three derives the normal
 * from screen-space derivatives of position and never reads one, which saves
 * twelve bytes a vertex — about a megabyte on a dense tile. Turning flat
 * shading off would leave the surface unlit.
 *
 * `FrontSide` is safe here where the extruded shells needed `DoubleSide`:
 * generated houses are closed volumes with outward winding, asserted by the
 * enclosed-volume test in `test/houses.spec.ts`.
 */
export const houseMaterial = new MeshStandardMaterial({
  color: new Color(0xffffff),
  vertexColors: true,
  roughness: 0.86,
  metalness: 0,
  side: FrontSide,
  flatShading: true,
});

/**
 * Night lighting for every window in the world: set once a frame.
 * `night` fades the lights in after sunset; `windowsLit` is the share of
 * windows switched on at this hour.
 */
export const houseNight = { night: { value: 0 }, windowsLit: { value: 0 } };

houseMaterial.onBeforeCompile = (shader) => {
  shader.uniforms.uNight = houseNight.night;
  shader.uniforms.uWindowsLit = houseNight.windowsLit;
  shader.uniforms.uRoofSnow = terrainWeather.roofSnow;
  shader.uniforms.uLampMap = lampLight.map;
  shader.uniforms.uLampOrigin = lampLight.origin;
  shader.uniforms.uLampInvExtent = lampLight.invExtent;
  shader.vertexShader = shader.vertexShader
    .replace('#include <common>', '#include <common>\nattribute float aGlow;\nvarying float vGlow;\nvarying float vWallY;\nvarying vec2 vHouseWorld;\nvarying vec3 vHousePos;')
    .replace('#include <worldpos_vertex>', '#include <worldpos_vertex>\n  vGlow = aGlow;\n  vWallY = transformed.y;\n  vHousePos = (modelMatrix * vec4(transformed, 1.0)).xyz;\n  vHouseWorld = vHousePos.xz;');
  shader.fragmentShader = shader.fragmentShader
    .replace('#include <common>', `#include <common>
uniform float uNight;
uniform float uWindowsLit;
uniform sampler2D uLampMap;
uniform vec2 uLampOrigin;
uniform float uLampInvExtent;
varying float vGlow;
varying float vWallY;
varying vec2 vHouseWorld;
varying vec3 vHousePos;
uniform float uRoofSnow;
${GLSL_SNOW_TOP}`)
    .replace('#include <color_fragment>', `#include <color_fragment>
  // Snow on the roofs, sills and ledges; a steep roof sheds most of it.
  diffuseColor.rgb = mix(diffuseColor.rgb, ${SNOW_TOP_COLOUR}, snowTop(vHousePos, uRoofSnow, 0.45));`)
    .replace('#include <emissivemap_fragment>', `#include <emissivemap_fragment>
  // Warm street-lamp illumination on the lower 7.5 m of building facades near streetlamps.
  if (uNight > 0.005 && vWallY < 7.5) {
    vec2 lampUv = (vHouseWorld - uLampOrigin) * uLampInvExtent;
    float wallLamp = (lampUv.x > 0.001 && lampUv.x < 0.999 && lampUv.y > 0.001 && lampUv.y < 0.999)
      ? texture2D(uLampMap, lampUv).r : 0.0;
    float streetBounce = smoothstep(7.5, 0.0, vWallY) * (0.04 + 0.75 * wallLamp) * uNight;
    totalEmissiveRadiance += diffuseColor.rgb * vec3(1.0, 0.52, 0.16) * streetBounce;
  }
  // A warm lamp-lit window, when its threshold is under tonight's share: varied from amber to soft cream room by room.
  if (vGlow > 0.001 && vGlow <= uWindowsLit) {
    float wWarm = fract(vGlow * 37.0);
    float wBright = 0.88 + 0.30 * fract(vGlow * 73.0);
    vec3 lamp = mix(vec3(1.0, 0.62, 0.28), vec3(1.0, 0.82, 0.50), wWarm);
    totalEmissiveRadiance += lamp * (3.4 * wBright * uNight);
  }`);
};

/**
 * Wrap a chunk's merged buffers in a mesh. Undefined for an empty chunk.
 *
 * Takes the three buffers rather than a `HouseGeometry` because the streaming
 * path has already consumed the ranges and probes by the time it gets here —
 * grounding used them — and passing empty stand-ins would only invite the
 * question of what they mean.
 */
export function createHouseMesh(
  positions: Float32Array,
  colors: Uint8Array,
  indices: Uint32Array,
  glow?: Uint8Array,
  /** The tile's sphere, when the caller has one: computing it is a pass over every vertex. */
  bounds?: Sphere,
): Mesh | undefined {
  if (indices.length === 0) return undefined;
  const buffer = new BufferGeometry();
  buffer.setAttribute('position', new BufferAttribute(positions, 3));
  // `normalized` matters: the bytes are linear-light 0..255 and have to arrive
  // in the shader as 0..1.
  buffer.setAttribute('color', new BufferAttribute(colors, 3, true));
  buffer.setAttribute('aGlow', new BufferAttribute(glow ?? new Uint8Array(positions.length / 3), 1, true));
  buffer.setIndex(new BufferAttribute(indices, 1));
  if (bounds !== undefined) buffer.boundingSphere = bounds;
  else buffer.computeBoundingSphere();
  const mesh = new Mesh(buffer, houseMaterial);
  mesh.matrixAutoUpdate = false;
  return mesh;
}

/** Most vertices in one piece of a tile's buildings: about two and a half megabytes to upload. */
const PIECE_VERTICES = 100_000;

/**
 * A tile's buildings as several meshes, each at most `PIECE_VERTICES`, so
 * they can be shown — and uploaded — a few a frame rather than all at once.
 * The pieces are views into the same buffers: nothing is copied but the
 * index, rebased onto each piece's own vertices.
 */
export function createHouseMeshes(
  positions: Float32Array,
  colors: Uint8Array,
  indices: Uint32Array,
  glow: Uint8Array | undefined,
  bounds: Sphere,
): Mesh[] {
  if (indices.length === 0) return [];
  const g = glow ?? new Uint8Array(positions.length / 3);
  const out: Mesh[] = [];
  let start = 0, lo = Infinity, hi = -Infinity;
  const close = (end: number) => {
    const index = new Uint32Array(end - start);
    for (let k = start; k < end; k++) index[k - start] = indices[k] - lo;
    const mesh = createHouseMesh(
      positions.subarray(lo * 3, (hi + 1) * 3), colors.subarray(lo * 3, (hi + 1) * 3), index,
      g.subarray(lo, hi + 1), pieceBounds(positions, lo, hi, bounds),
    );
    if (mesh !== undefined) out.push(mesh);
  };
  for (let i = 0; i + 2 < indices.length; i += 3) {
    const a = indices[i], b = indices[i + 1], c = indices[i + 2];
    const tlo = Math.min(a, b, c), thi = Math.max(a, b, c);
    // The worker writes each building's vertices together, so a triangle's
    // stay near the last's; a piece closes once its span grows past the limit.
    if (i > start && Math.max(hi, thi) - Math.min(lo, tlo) >= PIECE_VERTICES) {
      close(i);
      start = i; lo = Infinity; hi = -Infinity;
    }
    lo = Math.min(lo, tlo);
    hi = Math.max(hi, thi);
  }
  close(indices.length - indices.length % 3);
  return out;
}

/**
 * A piece's own sphere, around its vertices `lo..hi`: a piece is a stretch of
 * the tile, so from close above — the top-down view — most of a tile's pieces
 * are culled, and their shadows with them, rather than all drawn for the one
 * the camera sees. One pass for the box; the sphere is round it. Never larger
 * than the tile's.
 */
function pieceBounds(positions: Float32Array, lo: number, hi: number, tile: Sphere): Sphere {
  let x0 = Infinity, y0 = Infinity, z0 = Infinity, x1 = -Infinity, y1 = -Infinity, z1 = -Infinity;
  for (let i = lo * 3, end = (hi + 1) * 3; i < end; i += 3) {
    const x = positions[i], y = positions[i + 1], z = positions[i + 2];
    if (x < x0) x0 = x; if (x > x1) x1 = x;
    if (y < y0) y0 = y; if (y > y1) y1 = y;
    if (z < z0) z0 = z; if (z > z1) z1 = z;
  }
  const radius = Math.hypot(x1 - x0, y1 - y0, z1 - z0) / 2;
  if (!(radius < tile.radius)) return tile.clone();
  const sphere = tile.clone();
  sphere.center.set((x0 + x1) / 2, (y0 + y1) / 2, (z0 + z1) / 2);
  sphere.radius = radius;
  return sphere;
}

const centre = new Vector3();
const size = new Vector3();

/**
 * Build one mesh for an array of streamed boxes.
 *
 * The boxes are world-space, so each building's geometry is lifted onto its own
 * `box.min.y` after generation — a single add over that building's vertex
 * range, which is why `HouseGeometry` carries ranges at all.
 */
export function buildHouseChunk(
  items: HouseChunkItem[],
  options: Partial<HouseOptions> = {},
): Mesh | undefined {
  const blocks: HouseBlock[] = [];
  const bases: number[] = [];
  const embed = options.embed ?? 0;

  for (const item of items) {
    item.box.getCenter(centre);
    item.box.getSize(size);
    const block: HouseBlock = {
      centreX: centre.x,
      centreZ: centre.z,
      width: size.x,
      depth: size.z,
      height: size.y,
      rotation: item.rotation,
      seed: item.seed,
      use: item.use,
      roof: item.roof,
      floors: item.floors,
      floorHeight: item.floorHeight,
      year: item.year,
      wallColour: item.wallColour,
      roofColour: item.roofColour,
      towerHeight: item.towerHeight,
      attic: item.attic,
    };
    // Filtered here by the generator's own rule, so `bases[h]` still names the
    // building that `ranges[h]` describes.
    if (!isBuildableBlock(block)) continue;
    blocks.push(block);
    bases.push(item.box.min.y);
  }

  const geometry = buildHouses(blocks, { ...options, embed });
  // `buildHouses` skips degenerate blocks, so walk ranges rather than assuming
  // one range per input.
  const houses = geometry.ranges.length / 2;
  for (let h = 0; h < houses; h++) {
    const start = geometry.ranges[h * 2];
    const count = geometry.ranges[h * 2 + 1];
    const lift = bases[h] - embed;
    if (lift === 0) continue;
    for (let v = 0; v < count; v++) geometry.positions[(start + v) * 3 + 1] += lift;
  }

  return createHouseMesh(geometry.positions, geometry.colors, geometry.indices, geometry.glow);
}

/**
 * Release a chunk's GPU memory.
 *
 * The geometry is per-chunk and must go; the material is a shared singleton and
 * must not. Getting that backwards either leaks a buffer per tile — tens of
 * megabytes within a minute of walking — or disposes a material that every
 * other tile is still drawing with.
 */
export function disposeHouseChunk(mesh: Mesh | undefined): void {
  mesh?.geometry.dispose();
}
